import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const READ_BLOCK = 64
const WRITE_BLOCK = 16
const MEMORY_SIZE = 0x2008
const CHANNEL_COUNT = 128
const NAME_START = 0x1000
const WIDE_MASK = 0x02
const SCAN_MASK = 0x20

type WP970ISpec = {
  id: string
  vendor: 'Baofeng' | 'Retevis' | 'Rugged'
  model: string
  summary: string
  magic: readonly number[]
  nameLength: 6 | 7
  powerLevels: readonly string[]
  triBand?: boolean
  airband?: boolean
}

export type WP970IProfile = {
  protocol: 'wp970i'
  id: string
  vendor: 'Baofeng' | 'Retevis' | 'Rugged'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 0
  channelMaximum: 127
  nameLength: 6 | 7
  memorySize: number
  magic: readonly number[]
  powerLevels: readonly string[]
  triBand: boolean
  airband: boolean
}

function ascii(value: string): readonly number[] {
  return Array.from(value, (character) => character.charCodeAt(0) & 0xff)
}

const MAGIC_WP970I = [0x50, 0xbb, 0xff, 0x20, 0x14, 0x04, 0x13]
const MAGIC_UV9G = [0x50, 0xbb, 0xff, 0x20, 0x12, 0x05, 0x25]
const MAGIC_UVS9X3 = [0x50, 0xbb, 0xff, 0x20, 0x12, 0x07, 0x25]
const TRI_POWER = ['High', 'Med', 'Low'] as const
const DUAL_POWER = ['High', 'Low'] as const

const SPECS: readonly WP970ISpec[] = [
  {
    id: 'baofeng-bf-a58',
    vendor: 'Baofeng',
    model: 'BF-A58',
    summary: 'Baofeng BF-A58 waterproof dual-band handheld.',
    magic: MAGIC_WP970I,
    nameLength: 7,
    powerLevels: TRI_POWER,
  },
  {
    id: 'baofeng-uv-82wp',
    vendor: 'Baofeng',
    model: 'UV-82WP',
    summary: 'Baofeng UV-82WP waterproof dual-band handheld.',
    magic: MAGIC_WP970I,
    nameLength: 6,
    powerLevels: TRI_POWER,
  },
  {
    id: 'baofeng-gt-3wp',
    vendor: 'Baofeng',
    model: 'GT-3WP',
    summary: 'Baofeng GT-3WP waterproof dual-band handheld.',
    magic: MAGIC_WP970I,
    nameLength: 7,
    powerLevels: TRI_POWER,
  },
  {
    id: 'retevis-rt6',
    vendor: 'Retevis',
    model: 'RT6',
    summary: 'Retevis RT6 waterproof dual-band handheld.',
    magic: MAGIC_WP970I,
    nameLength: 6,
    powerLevels: TRI_POWER,
  },
  {
    id: 'baofeng-bf-a58s',
    vendor: 'Baofeng',
    model: 'BF-A58S',
    summary: 'Baofeng BF-A58S tri-band waterproof handheld.',
    magic: MAGIC_WP970I,
    nameLength: 7,
    powerLevels: DUAL_POWER,
    triBand: true,
  },
  {
    id: 'baofeng-uv-s9x3',
    vendor: 'Baofeng',
    model: 'UV-S9X3',
    summary: 'Baofeng UV-S9X3 tri-band waterproof handheld.',
    magic: MAGIC_UVS9X3,
    nameLength: 7,
    powerLevels: DUAL_POWER,
    triBand: true,
  },
  {
    id: 'baofeng-5rx',
    vendor: 'Baofeng',
    model: '5RX',
    summary: 'Baofeng 5RX tri-band handheld with airband receive.',
    magic: MAGIC_UVS9X3,
    nameLength: 7,
    powerLevels: DUAL_POWER,
    triBand: true,
    airband: true,
  },
  {
    id: 'baofeng-uv-9r',
    vendor: 'Baofeng',
    model: 'UV-9R',
    summary: 'Baofeng UV-9R waterproof dual-band handheld.',
    magic: MAGIC_WP970I,
    nameLength: 7,
    powerLevels: TRI_POWER,
  },
  {
    id: 'baofeng-uv-9g',
    vendor: 'Baofeng',
    model: 'UV-9G',
    summary: 'Baofeng UV-9G GMRS waterproof handheld.',
    magic: MAGIC_UV9G,
    nameLength: 7,
    powerLevels: TRI_POWER,
  },
  {
    id: 'rugged-rh5x',
    vendor: 'Rugged',
    model: 'RH5X',
    summary: 'Rugged RH5X waterproof dual-band handheld.',
    magic: MAGIC_WP970I,
    nameLength: 7,
    powerLevels: TRI_POWER,
  },
  {
    id: 'baofeng-uv-82iii',
    vendor: 'Baofeng',
    model: 'UV-82III',
    summary: 'Baofeng UV-82III tri-band waterproof handheld.',
    magic: MAGIC_WP970I,
    nameLength: 7,
    powerLevels: DUAL_POWER,
    triBand: true,
  },
  {
    id: 'baofeng-uv-9r-pro',
    vendor: 'Baofeng',
    model: 'UV-9R Pro',
    summary: 'Baofeng UV-9R Pro waterproof dual-band handheld.',
    magic: MAGIC_WP970I,
    nameLength: 7,
    powerLevels: TRI_POWER,
  },
]

export const WP970I_PROFILES: readonly WP970IProfile[] = SPECS.map((spec) => ({
  protocol: 'wp970i',
  id: spec.id,
  vendor: spec.vendor,
  model: spec.model,
  summary: spec.summary,
  tags: [
    'Experimental',
    'Read + write',
    '128 channels',
    `${spec.nameLength}-character names`,
  ],
  baudRate: 9600,
  channelMinimum: 0,
  channelMaximum: 127,
  nameLength: spec.nameLength,
  memorySize: MEMORY_SIZE,
  magic: spec.magic,
  powerLevels: spec.powerLevels,
  triBand: spec.triBand ?? false,
  airband: spec.airband ?? false,
}))

function emptyChannel(number: number, powerLevels: readonly string[]): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: powerLevels[0],
    skip: false,
  }
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(raw: Uint8Array): DecodedTone {
  const value = raw[0] | (raw[1] << 8)
  if (value === 0 || value === 0xffff) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value >= 0x0258) {
    return { mode: 'ctcss', ctcss: value / 10, dtcs: null, polarity: 'N' }
  }
  const reversed = value > 0x69
  return {
    mode: 'dtcs',
    ctcss: null,
    dtcs: DTCS_CODES[reversed ? value - 0x6a : value - 1] ?? null,
    polarity: reversed ? 'R' : 'N',
  }
}

function encodeTone(channel: WebChannel): readonly [number, number] {
  let value = 0
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    if (!CTCSS_TONES.includes(channel.ctcss as (typeof CTCSS_TONES)[number])) {
      throw new Error('That CTCSS tone is not supported by this radio.')
    }
    value = Math.round(channel.ctcss * 10)
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = DTCS_CODES.indexOf(
      channel.dtcs as (typeof DTCS_CODES)[number],
    )
    if (index < 0) throw new Error('That DCS code is not supported by this radio.')
    value = index + 1 + (channel.dtcsPolarity === 'R' ? 0x69 : 0)
  }
  return [value & 0xff, (value >> 8) & 0xff]
}

function toneMatches(channel: WebChannel, tone: DecodedTone): boolean {
  if (channel.toneMode === 'none') return tone.mode === 'none'
  if (channel.toneMode === 'ctcss') {
    return tone.mode === 'ctcss' && tone.ctcss === channel.ctcss
  }
  return (
    tone.mode === 'dtcs' &&
    tone.dtcs === channel.dtcs &&
    tone.polarity === channel.dtcsPolarity
  )
}

function decodeName(raw: Uint8Array): string {
  return String.fromCharCode(
    ...raw.map((value) => (value === 0xff || value === 0 ? 0x20 : value)),
  ).trim()
}

function channelOffset(number: number): number {
  return number * 16
}

function nameOffset(number: number): number {
  return NAME_START + number * 16
}

export function parseWP970IChannel(
  image: Uint8Array,
  profile: WP970IProfile,
  number: number,
): WebChannel {
  const offset = channelOffset(number)
  const raw = image.slice(offset, offset + 16)
  if (raw.byteLength !== 16 || raw[0] === 0xff) {
    return emptyChannel(number, profile.powerLevels)
  }
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number, profile.powerLevels)
  const txFrequency = raw[4] === 0xff ? null : decodeLbcd(raw.slice(4, 8))
  const txTone = decodeTone(raw.slice(10, 12))
  const rxtone = decodeTone(raw.slice(8, 10))
  const selected = txTone.mode === 'none' ? rxtone : txTone
  const am =
    profile.airband &&
    rxFrequency >= 108_000_000 &&
    rxFrequency < 136_000_000
  return {
    number,
    empty: false,
    name: decodeName(
      image.slice(nameOffset(number), nameOffset(number) + profile.nameLength),
    ),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: am ? 'AM' : raw[15] & WIDE_MASK ? 'FM' : 'NFM',
    power: profile.powerLevels[raw[14] & 0x03] ?? profile.powerLevels[0],
    skip: (raw[15] & SCAN_MASK) === 0,
  }
}

export function parseWP970IChannels(
  image: Uint8Array,
  profile: WP970IProfile,
): WebChannel[] {
  return Array.from(
    { length: CHANNEL_COUNT },
    (_, number) => parseWP970IChannel(image, profile, number),
  )
}

export function applyWP970IChannel(
  image: Uint8Array,
  profile: WP970IProfile,
  channel: WebChannel,
): void {
  if (channel.number < 0 || channel.number >= CHANNEL_COUNT) {
    throw new Error('Channel must be between 0 and 127.')
  }
  const offset = channelOffset(channel.number)
  const raw = image.subarray(offset, offset + 16)
  const nameStart = nameOffset(channel.number)
  if (channel.empty) {
    raw.fill(0xff)
    image.fill(0xff, nameStart, nameStart + profile.nameLength)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if (raw[0] === 0xff) {
    raw.fill(0xff)
    raw.fill(0, 0, 8)
  }
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  if (
    !toneMatches(channel, decodeTone(raw.slice(10, 12))) ||
    (!toneMatches(channel, decodeTone(raw.slice(8, 10))) &&
      decodeTone(raw.slice(8, 10)).mode !== 'none')
  ) {
    const tone = encodeTone(channel)
    raw.set(tone, 8)
    raw.set(tone, 10)
  }
  raw[14] =
    (raw[14] & 0xfc) |
    (Math.max(0, profile.powerLevels.indexOf(channel.power)) & 0x03)
  raw[15] =
    (raw[15] & ~(WIDE_MASK | SCAN_MASK)) |
    (channel.mode === 'FM' ? WIDE_MASK : 0) |
    (channel.skip ? 0 : SCAN_MASK)
  image.fill(0xff, nameStart, nameStart + profile.nameLength)
  for (
    let index = 0;
    index < Math.min(profile.nameLength, channel.name.length);
    index += 1
  ) {
    image[nameStart + index] = channel.name.charCodeAt(index) & 0xff
  }
}

async function readAck(
  transport: RadioTransport,
  message: string,
): Promise<void> {
  if ((await transport.readExact(1, 3000))[0] !== ACK) throw new Error(message)
}

async function enterProgrammingMode(
  profile: WP970IProfile,
  transport: RadioTransport,
): Promise<Uint8Array> {
  await transport.discardInput()
  await transport.write(Uint8Array.from(profile.magic))
  await readAck(transport, 'The radio refused WP970I programming mode.')
  await transport.write(Uint8Array.of(0x02))
  const identity = await transport.readExact(8, 3000)
  if (identity[0] !== 0xaa || identity[7] !== 0xdd) {
    throw new Error('The radio returned an invalid WP970I identification frame.')
  }
  await transport.write(Uint8Array.of(ACK))
  await readAck(transport, 'The radio rejected the WP970I clone session.')
  return identity
}

function commandFrame(command: number, address: number, size: number): Uint8Array {
  return Uint8Array.of(
    command,
    (address >> 8) & 0xff,
    address & 0xff,
    size,
  )
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  await transport.write(commandFrame(0x53, address, READ_BLOCK))
  await readAck(
    transport,
    `The radio refused WP970I read block 0x${address.toString(16)}.`,
  )
  const response = await transport.readExact(READ_BLOCK + 4, 4000)
  if (
    response[0] !== 0x58 ||
    response[1] !== ((address >> 8) & 0xff) ||
    response[2] !== (address & 0xff) ||
    response[3] !== READ_BLOCK
  ) {
    throw new Error(`Unexpected WP970I read reply at 0x${address.toString(16)}.`)
  }
  await transport.write(Uint8Array.of(ACK))
  return response.slice(4)
}

async function writeBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const frame = new Uint8Array(4 + WRITE_BLOCK)
  frame.set(commandFrame(0x58, address, WRITE_BLOCK))
  frame.set(data, 4)
  await transport.write(frame)
  await readAck(
    transport,
    `The radio rejected WP970I write block 0x${address.toString(16)}.`,
  )
}

export async function readWP970IRadioWithTransport(
  profile: WP970IProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const identity = await enterProgrammingMode(profile, transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    const total = 0x2000 / READ_BLOCK
    for (let index = 0; index < total; index += 1) {
      const address = index * READ_BLOCK
      image.set(await readBlock(transport, address), address)
      onProgress?.({
        current: index + 1,
        total,
        label: 'Reading WP970I memory',
      })
    }
    image.set(identity, 0x2000)
    return { image, reportedModel: profile.model }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
  }
}

export async function writeWP970IRadioWithTransport(
  profile: WP970IProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than WP970I memory.')
  }
  const ranges = [
    [0x0000, 0x0df0],
    [0x0e00, 0x1800],
    [0x1ee0, 0x1ef0],
    [0x1f60, 0x1f70],
    [0x1f80, 0x1f90],
    [0x1fc0, 0x1fd0],
  ] as const
  const addresses = ranges.flatMap(([start, end]) =>
    Array.from(
      { length: (end - start) / WRITE_BLOCK },
      (_, index) => start + index * WRITE_BLOCK,
    ),
  )
  await enterProgrammingMode(profile, transport)
  try {
    for (let index = 0; index < addresses.length; index += 1) {
      const address = addresses[index]
      await writeBlock(
        transport,
        address,
        image.slice(address, address + WRITE_BLOCK),
      )
      onProgress?.({
        current: index + 1,
        total: addresses.length,
        label: 'Writing WP970I memory',
      })
    }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
  }
}
